import { useEffect, useState } from 'react'
import {
  Inbox,
  Clock3,
  CheckCircle2,
  Archive,
  RefreshCw,
  Search,
  Bell,
  X,
  Sparkles,
} from 'lucide-react'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { Breadcrumb } from '../../components/ui/Breadcrumb'
import { shellAssets } from '../../constants/assets'
import { dashboardRoutes } from '../../constants/routes'
import { useDashboardBadges } from '../../context/DashboardBadgeContext'
import { useEnquiries, type EnquiryFilterStatus } from '../../hooks/useEnquiries'
import { EnquiryCard } from './EnquiryCard'
import { EnquiryDetailModal } from './EnquiryDetailModal'
import { parseEnquiry } from './parseEnquiry'

const currentUser = {
  name: 'Admin',
  email: 'Admin@123gmail.com',
  avatar: shellAssets.avatar,
}

export const EnquiryPage = () => {
  const { markEnquiryAsSeen } = useDashboardBadges()
  const {
    enquiries,
    filterStatus,
    setFilterStatus,
    searchQuery,
    setSearchQuery,
    selectedEnquiry,
    setSelectedEnquiry,
    stats,
    isLoading,
    error,
    latestIncoming,
    dismissLatestIncoming,
    refresh,
    setEnquiryStatus,
    removeEnquiry,
  } = useEnquiries()

  const [isRefreshing, setIsRefreshing] = useState(false)

  // Clear sidebar indicator when admin visits the enquiry page
  useEffect(() => {
    markEnquiryAsSeen()
  }, [markEnquiryAsSeen])

  const handleManualRefresh = async () => {
    setIsRefreshing(true)
    await refresh()
    setTimeout(() => setIsRefreshing(false), 400)
  }

  const latestDetails = latestIncoming ? parseEnquiry(latestIncoming) : null

  return (
    <DashboardLayout
      activeNavId="enquiries"
      contentGap={21}
      subtitle={
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: dashboardRoutes.overview },
            { label: 'Enquiries' },
          ]}
        />
      }
      title="Customer Enquiries"
      user={currentUser}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-5 pb-8 overflow-y-auto">
        {/* Real-time Toast Banner when new enquiry arrives */}
        {latestIncoming && latestDetails && (
          <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-emerald-600 to-teal-700 p-4 text-white shadow-lg animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-full bg-white/20">
                <Bell className="size-5 animate-bounce" />
              </div>
              <div>
                <p className="text-sm font-semibold flex items-center gap-1.5">
                  <Sparkles className="size-4" />
                  New Private Tour Enquiry Received!
                </p>
                <p className="text-xs text-white/90">
                  {latestDetails.customerName} enquired for{' '}
                  <span className="font-semibold underline">{latestDetails.tourTitle}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSelectedEnquiry(latestIncoming)
                  dismissLatestIncoming()
                }}
                className="rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-50 transition-colors shadow-sm"
              >
                View Details
              </button>
              <button
                onClick={dismissLatestIncoming}
                className="rounded-lg p-1 text-white/80 hover:bg-white/10 hover:text-white"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <button
            onClick={() => setFilterStatus('all')}
            className={`flex items-center gap-3.5 rounded-2xl bg-white p-4 shadow-sm border transition-all text-left ${
              filterStatus === 'all'
                ? 'border-emerald-600 ring-2 ring-emerald-600/20'
                : 'border-gray-200/80 hover:border-gray-300'
            }`}
          >
            <div className="flex size-11 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
              <Inbox className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">All Enquiries</p>
              <p className="text-xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </button>

          <button
            onClick={() => setFilterStatus('open')}
            className={`flex items-center gap-3.5 rounded-2xl bg-white p-4 shadow-sm border transition-all text-left ${
              filterStatus === 'open'
                ? 'border-emerald-600 ring-2 ring-emerald-600/20'
                : 'border-gray-200/80 hover:border-emerald-200'
            }`}
          >
            <div className="flex size-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <Clock3 className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">Open / Pending</p>
              <p className="text-xl font-bold text-emerald-700">{stats.open}</p>
            </div>
          </button>

          <button
            onClick={() => setFilterStatus('replied')}
            className={`flex items-center gap-3.5 rounded-2xl bg-white p-4 shadow-sm border transition-all text-left ${
              filterStatus === 'replied'
                ? 'border-emerald-600 ring-2 ring-emerald-600/20'
                : 'border-gray-200/80 hover:border-blue-200'
            }`}
          >
            <div className="flex size-11 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
              <CheckCircle2 className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">Replied</p>
              <p className="text-xl font-bold text-blue-700">{stats.replied}</p>
            </div>
          </button>

          <button
            onClick={() => setFilterStatus('closed')}
            className={`flex items-center gap-3.5 rounded-2xl bg-white p-4 shadow-sm border transition-all text-left ${
              filterStatus === 'closed'
                ? 'border-emerald-600 ring-2 ring-emerald-600/20'
                : 'border-gray-200/80 hover:border-gray-300'
            }`}
          >
            <div className="flex size-11 items-center justify-center rounded-xl bg-gray-100 text-gray-600">
              <Archive className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">Closed</p>
              <p className="text-xl font-bold text-gray-700">{stats.closed}</p>
            </div>
          </button>
        </div>

        {/* Toolbar: Search & Status Tabs */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 rounded-2xl bg-white p-3.5 shadow-sm border border-gray-200/80">
          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
            {(
              [
                { id: 'all', label: 'All', count: stats.total },
                { id: 'open', label: 'Open', count: stats.open },
                { id: 'replied', label: 'Replied', count: stats.replied },
                { id: 'closed', label: 'Closed', count: stats.closed },
              ] as { id: EnquiryFilterStatus; label: string; count: number }[]
            ).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilterStatus(tab.id)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                  filterStatus === tab.id
                    ? 'bg-emerald-700 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`text-[11px] px-1.5 py-0.2 rounded-full ${
                    filterStatus === tab.id
                      ? 'bg-emerald-900/60 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search & Refresh */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, tour, email..."
                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 pl-9 pr-3 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:border-emerald-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-600 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>

            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing || isLoading}
              className="flex items-center justify-center size-9 rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors shrink-0 disabled:opacity-50"
              title="Refresh enquiries"
            >
              <RefreshCw
                className={`size-4 ${isRefreshing || isLoading ? 'animate-spin text-emerald-700' : ''}`}
              />
            </button>
          </div>
        </div>

        {/* Error Notice */}
        {error && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {error}
          </div>
        )}

        {/* Loading State */}
        {isLoading && enquiries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100">
            <RefreshCw className="size-8 text-emerald-700 animate-spin mb-3" />
            <p className="text-sm font-medium text-gray-600">Loading enquiries...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && enquiries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-200/80 p-8 text-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 mb-4">
              <Inbox className="size-8" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">No enquiries found</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-sm">
              {searchQuery
                ? `No enquiries matched your search for "${searchQuery}". Try a different keyword.`
                : filterStatus !== 'all'
                ? `There are currently no enquiries marked as "${filterStatus}".`
                : 'Customer enquiries submitted from private van tours and the website will appear here in real time.'}
            </p>
            {(searchQuery || filterStatus !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('')
                  setFilterStatus('all')
                }}
                className="mt-4 px-4 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs font-semibold text-gray-700 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* Enquiries Grid */}
        {enquiries.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {enquiries.map((enquiry) => (
              <EnquiryCard
                key={enquiry.id}
                enquiry={enquiry}
                onSelect={setSelectedEnquiry}
                onStatusChange={setEnquiryStatus}
                onDelete={removeEnquiry}
              />
            ))}
          </div>
        )}

        {/* Detailed Modal */}
        <EnquiryDetailModal
          enquiry={selectedEnquiry}
          onClose={() => setSelectedEnquiry(null)}
          onStatusChange={setEnquiryStatus}
          onDelete={removeEnquiry}
        />
      </div>
    </DashboardLayout>
  )
}

export default EnquiryPage
